// src/pages/restaurant-tables/LocationTables.jsx
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  LinearProgress,
  Alert,
  Snackbar,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Fab,
  Divider,
  Fade,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as AvailableIcon,
  Cancel as OccupiedIcon,
  EventAvailable as ReservedIcon,
  Build as MaintenanceIcon,
  Visibility as ViewIcon,
  Restaurant as TableIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  TableRestaurant as TableRestaurantIcon,
} from '@mui/icons-material';

// Lazy load sidebars for better initial load
const AfricanSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/african/Sidebar'));
const ContinentalSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/continental/Sidebar'));
const LoungeSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/lounge/Sidebar'));
const Header = lazy(() => import('../../../components/RestaurantPOS/manager/Header'));
const Footer = lazy(() => import('../../../components/RestaurantPOS/manager/Footer'));
const Preloader = lazy(() => import('../../../theme/Preloader'));

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// ============================================
// UTILITY FUNCTIONS & CONSTANTS
// ============================================

const TABLE_STATUS = {
  AVAILABLE: 'AVAILABLE',
  OCCUPIED: 'OCCUPIED',
  RESERVED: 'RESERVED',
  MAINTENANCE: 'MAINTENANCE'
};

const STATUS_CONFIG = {
  [TABLE_STATUS.AVAILABLE]: {
    label: 'Available',
    color: 'success',
    icon: AvailableIcon,
    bgColor: '#e8f5e9'
  },
  [TABLE_STATUS.OCCUPIED]: {
    label: 'Occupied',
    color: 'error',
    icon: OccupiedIcon,
    bgColor: '#ffebee'
  },
  [TABLE_STATUS.RESERVED]: {
    label: 'Reserved',
    color: 'warning',
    icon: ReservedIcon,
    bgColor: '#fff3e0'
  },
  [TABLE_STATUS.MAINTENANCE]: {
    label: 'Maintenance',
    color: 'default',
    icon: MaintenanceIcon,
    bgColor: '#f5f5f5'
  }
};

// Location configurations - memoized
const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: '#2E7D32',
    lightColor: '#4CAF50',
    icon: '🌍',
    sidebar: AfricanSidebar,
    path: '/african-tables'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: '#1565C0',
    lightColor: '#1976D2',
    icon: '🌐',
    sidebar: ContinentalSidebar,
    path: '/continental-tables'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: '#6A1B9A',
    lightColor: '#9C27B0',
    icon: '🍸',
    sidebar: LoungeSidebar,
    path: '/lounge-tables'
  }
};

// Memoized status options
const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, config]) => ({
  value,
  label: config.label
}));

// ============================================
// TOKEN UTILITY FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    return 0;
  }
};

// ============================================
// SUB-COMPONENTS FOR BETTER PERFORMANCE
// ============================================

// Memoized Stats Card Component
const StatsCard = React.memo(({ stats, locationColor }) => (
  <div className="stats" style={{ marginBottom: '20px' }}>
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="card-label" style={{ fontSize: '13px' }}>Total Tables</div>
      <div className="card-value" style={{ fontSize: '28px', fontWeight: 'bold' }}>{stats.total_tables}</div>
    </div>
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="card-label" style={{ fontSize: '13px' }}>Available</div>
      <div className="card-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4caf50' }}>
        {stats.available}
      </div>
    </div>
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="card-label" style={{ fontSize: '13px' }}>Occupied</div>
      <div className="card-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#f44336' }}>
        {stats.occupied}
      </div>
    </div>
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="card-label" style={{ fontSize: '13px' }}>Reserved</div>
      <div className="card-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff9800' }}>
        {stats.reserved}
      </div>
    </div>
  </div>
));

// Memoized Table Card Component
const TableCard = React.memo(({ table, onView, onEdit, onDelete, getStatusChip }) => {
  const statusKey = table.status?.toUpperCase() || TABLE_STATUS.AVAILABLE;
  const statusConfig = STATUS_CONFIG[statusKey] || STATUS_CONFIG[TABLE_STATUS.AVAILABLE];
  const borderColor = statusConfig.color === 'success' ? '#4caf50' :
                      statusConfig.color === 'error' ? '#f44336' :
                      statusConfig.color === 'warning' ? '#ff9800' : '#9e9e9e';

  return (
    <div className="panel" style={{ 
      borderLeft: `4px solid ${borderColor}`,
      transition: 'all 0.3s ease',
      cursor: 'default'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div>
          <Typography variant="h6" fontWeight="bold">
            {table.table_name}
          </Typography>
        </div>
        {getStatusChip(table.status)}
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
        <GroupIcon fontSize="small" color="action" />
        <Typography variant="body2">
          Capacity: <strong>{table.capacity}</strong> people
        </Typography>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
        <CalendarIcon fontSize="small" color="action" />
        <Typography variant="caption" color="textSecondary">
          Created: {table.created_date ? new Date(table.created_date).toLocaleDateString() : 'N/A'}
        </Typography>
      </div>
      
      <Divider style={{ margin: '12px 0' }} />
      
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => onView(table)}
            color="primary"
          >
            <ViewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => onEdit(table)}
            color="info"
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={() => onDelete(table)}
            color="error"
            disabled={table.status?.toUpperCase() === TABLE_STATUS.OCCUPIED}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
});

// ============================================
// MAIN COMPONENT
// ============================================

const LocationTables = ({ locationKey = 'african' }) => {
  // ============================================
  // STATE MANAGEMENT - Optimized
  // ============================================

  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Tables');
  const [isLoading, setIsLoading] = useState(true);
  const [locationInfo, setLocationInfo] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total_tables: 0,
    available: 0,
    occupied: 0,
    reserved: 0,
    maintenance: 0
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states - Optimized with lazy initialization
  const [dialogState, setDialogState] = useState({
    open: false,
    mode: 'create',
    table: null,
    loading: false
  });

  const [formData, setFormData] = useState({
    table_name: '',
    location_id: '',
    status: TABLE_STATUS.AVAILABLE,
    capacity: 4,
    description: '',
    created_date: new Date().toISOString().split('T')[0]
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    table: null,
    deleting: false
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get location config - memoized
  const locationConfig = useMemo(() => 
    LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african,
    [locationKey]
  );
  
  const { name: locationName, id: locationIdValue, color: locationColor } = locationConfig;
  const SidebarComponent = locationConfig.sidebar;

  // ============================================
  // TOKEN MANAGEMENT FUNCTIONS
  // ============================================

  const getAuthToken = useCallback(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || isTokenExpired(token)) {
        // Token expired or invalid
        return null;
      }
      return token;
    } catch (err) {
      console.error('[LocationTables] Error getting auth token:', err);
      return null;
    }
  }, []);

  const refreshAuthToken = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        return data.access_token;
      }
      throw new Error('No access token in refresh response');
    } catch (err) {
      console.error('[LocationTables] Token refresh failed:', err);
      // Clear all auth data and redirect to login
      localStorage.clear();
      window.location.href = '/restaurantAuth';
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // ============================================
  // API CALLS - Enhanced with token refresh
  // ============================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    let token = getAuthToken();
    
    // If token is invalid, try to refresh
    if (!token) {
      console.log('[LocationTables] Token invalid, attempting refresh...');
      token = await refreshAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${FULL_API_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // If 401, try refreshing once
      if (response.status === 401) {
        console.log('[LocationTables] Received 401, attempting token refresh...');
        const newToken = await refreshAuthToken();
        if (newToken) {
          // Retry the request with new token
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, {
            ...options,
            headers,
          });
          if (retryResponse.ok) {
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return await retryResponse.json();
            }
            return { message: await retryResponse.text() };
          }
          // If retry fails with 401, throw error
          if (retryResponse.status === 401) {
            throw new Error('Authentication failed after refresh');
          }
        }
        throw new Error('Authentication failed');
      }

      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || 'Non-JSON response received' };
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (err) {
      console.error('[LocationTables] API Error:', err);
      throw err;
    }
  }, [getAuthToken, refreshAuthToken]);

  // ============================================
  // FETCH FUNCTIONS - Optimized with retry logic
  // ============================================

  const fetchTables = useCallback(async () => {
    setLoadingTables(true);
    try {
      const data = await apiRequest(`/tables/location/${locationKey}`);
      const tablesData = Array.isArray(data) ? data : [];
      
      const filteredData = tablesData.filter(table => 
        table.location_id === locationIdValue || 
        (table.location && table.location.toLowerCase() === locationKey)
      );
      
      setTables(filteredData);
      setFilteredTables(filteredData);
      await fetchLocationStats();
    } catch (err) {
      console.error(`[LocationTables] Error fetching tables:`, err);
      
      // Check if error is authentication related
      if (err.message === 'Authentication required' || err.message === 'Authentication failed after refresh') {
        setSnackbar({
          open: true,
          message: 'Session expired. Please login again.',
          severity: 'error'
        });
        // Redirect to login after a delay
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: `Failed to fetch ${locationName} tables: ${err.message}`,
          severity: 'error'
        });
      }
    } finally {
      setLoadingTables(false);
      setIsLoading(false);
    }
  }, [apiRequest, locationKey, locationName, locationIdValue]);

  const fetchLocationStats = useCallback(async () => {
    try {
      const statsData = await apiRequest(`/tables/location/${locationKey}/stats`);
      setStats({
        total_tables: statsData.total_tables || 0,
        available: statsData.available || 0,
        occupied: statsData.occupied || 0,
        reserved: statsData.reserved || 0,
        maintenance: statsData.maintenance || 0
      });
      setLocationInfo({
        id: statsData.location_id,
        name: statsData.location_name,
        description: statsData.location_description
      });
    } catch (err) {
      console.error(`[LocationTables] Error fetching stats:`, err);
    }
  }, [apiRequest, locationKey]);

  // ============================================
  // DUPLICATE CHECK - Optimized with Set
  // ============================================

  const isDuplicateTableName = useCallback((name, currentId = null) => {
    if (!name || !name.trim()) return false;
    const trimmedName = name.trim().toLowerCase();
    return tables.some(t => 
      t.table_name?.toLowerCase() === trimmedName && 
      t.id !== (currentId || -1)
    );
  }, [tables]);

  // ============================================
  // CRUD OPERATIONS - Optimized
  // ============================================

  const handleCreateTable = useCallback(async (data) => {
    try {
      const submitData = { ...data, location_id: locationIdValue };
      const result = await apiRequest('/tables/', {
        method: 'POST',
        body: JSON.stringify(submitData)
      });
      
      setSnackbar({
        open: true,
        message: `Table "${result.table_name}" created successfully!`,
        severity: 'success'
      });
      
      await fetchTables();
      handleCloseDialog();
      return result;
    } catch (err) {
      const errorMessage = err.message || '';
      let userFriendlyMessage = `Failed to create table.`;
      
      if (errorMessage.toLowerCase().includes('already exists') || 
          errorMessage.toLowerCase().includes('duplicate entry')) {
        userFriendlyMessage = `A table named "${data.table_name}" already exists. Please use a different name.`;
      } else if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        userFriendlyMessage = `Failed to create table: ${errorMessage}`;
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error'
      });
      
      throw err;
    }
  }, [apiRequest, fetchTables, locationIdValue]);

  const handleUpdateTable = useCallback(async (id, data) => {
    try {
      const submitData = { ...data, location_id: locationIdValue };
      const result = await apiRequest(`/tables/${id}`, {
        method: 'PUT',
        body: JSON.stringify(submitData)
      });
      
      setSnackbar({
        open: true,
        message: `Table "${result.table_name}" updated successfully!`,
        severity: 'success'
      });
      
      await fetchTables();
      handleCloseDialog();
      return result;
    } catch (err) {
      const errorMessage = err.message || '';
      let userFriendlyMessage = `Failed to update table.`;
      
      if (errorMessage.toLowerCase().includes('already exists') || 
          errorMessage.toLowerCase().includes('duplicate entry')) {
        userFriendlyMessage = `A table named "${data.table_name}" already exists. Please use a different name.`;
      } else if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        userFriendlyMessage = `Failed to update table: ${errorMessage}`;
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error'
      });
      
      throw err;
    }
  }, [apiRequest, fetchTables, locationIdValue]);

  const handleDeleteTable = useCallback(async (id) => {
    setDeleteDialog(prev => ({ ...prev, deleting: true }));
    try {
      await apiRequest(`/tables/${id}`, {
        method: 'DELETE'
      });
      
      setSnackbar({
        open: true,
        message: 'Table deleted successfully!',
        severity: 'success'
      });
      
      await fetchTables();
      setDeleteDialog({ open: false, table: null, deleting: false });
    } catch (err) {
      console.error(`[LocationTables] Error deleting table:`, err);
      const errorMessage = err.message || '';
      let userFriendlyMessage = `Failed to delete table: ${errorMessage}`;
      
      if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error'
      });
      setDeleteDialog(prev => ({ ...prev, deleting: false }));
    }
  }, [apiRequest, fetchTables]);

  // ============================================
  // DIALOG HANDLERS - Optimized
  // ============================================

  const handleOpenDialog = useCallback((mode, table = null) => {
    const today = new Date().toISOString().split('T')[0];
    
    let newFormData;
    if (mode === 'create') {
      newFormData = {
        table_name: '',
        location_id: locationIdValue,
        status: TABLE_STATUS.AVAILABLE,
        capacity: 4,
        description: '',
        created_date: today
      };
    } else if (table) {
      newFormData = {
        table_name: table.table_name || '',
        location_id: locationIdValue,
        status: table.status || TABLE_STATUS.AVAILABLE,
        capacity: table.capacity || 4,
        description: table.description || '',
        created_date: table.created_date || today
      };
    }
    
    setFormData(newFormData);
    setFormErrors({});
    setSubmitting(false);
    setDialogState({
      open: true,
      mode,
      table,
      loading: false
    });
  }, [locationIdValue]);

  const handleCloseDialog = useCallback(() => {
    setDialogState({
      open: false,
      mode: 'create',
      table: null,
      loading: false
    });
    setFormErrors({});
    setSubmitting(false);
  }, []);

  const handleOpenDeleteDialog = useCallback((table) => {
    setDeleteDialog({
      open: true,
      table,
      deleting: false
    });
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialog({
      open: false,
      table: null,
      deleting: false
    });
  }, []);

  // ============================================
  // FORM HANDLERS - Optimized
  // ============================================

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    const trimmedName = formData.table_name?.trim() || '';
    
    if (!trimmedName) {
      errors.table_name = 'Table name is required';
    } else if (trimmedName.length > 50) {
      errors.table_name = 'Table name must be 50 characters or less';
    } else if (isDuplicateTableName(trimmedName, dialogState.table?.id)) {
      errors.table_name = `"${trimmedName}" already exists. Please use a different name.`;
    }
    
    if (!formData.status) {
      errors.status = 'Status is required';
    }
    
    if (!formData.capacity || formData.capacity < 1) {
      errors.capacity = 'Capacity must be at least 1';
    } else if (formData.capacity > 20) {
      errors.capacity = 'Capacity must be 20 or less';
    }
    
    if (!formData.created_date) {
      errors.created_date = 'Created date is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, isDuplicateTableName, dialogState.table]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const submitData = {
      ...formData,
      table_name: formData.table_name.trim(),
      location_id: locationIdValue
    };
    
    setSubmitting(true);
    
    try {
      if (dialogState.mode === 'create') {
        await handleCreateTable(submitData);
      } else if (dialogState.mode === 'edit' && dialogState.table) {
        await handleUpdateTable(dialogState.table.id, submitData);
      }
    } catch (err) {
      console.error('[LocationTables] Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  }, [formData, dialogState, validateForm, handleCreateTable, handleUpdateTable, locationIdValue]);

  // ============================================
  // RENDER HELPERS - Memoized
  // ============================================

  const getStatusChip = useCallback((status) => {
    const config = STATUS_CONFIG[status?.toUpperCase()] || STATUS_CONFIG[TABLE_STATUS.AVAILABLE];
    const Icon = config.icon;
    
    return (
      <Chip
        icon={<Icon />}
        label={config.label}
        color={config.color}
        size="small"
        sx={{
          fontWeight: 'medium',
          '& .MuiChip-icon': {
            fontSize: '16px'
          }
        }}
      />
    );
  }, []);

  const getTableNameHelperText = useCallback(() => {
    const trimmedName = formData.table_name?.trim() || '';
    if (formErrors.table_name) {
      return formErrors.table_name;
    }
    if (trimmedName && isDuplicateTableName(trimmedName, dialogState.table?.id)) {
      return `⚠️ "${trimmedName}" already exists`;
    }
    return '';
  }, [formData.table_name, formErrors.table_name, isDuplicateTableName, dialogState.table]);

  const hasTableNameError = useCallback(() => {
    const trimmedName = formData.table_name?.trim() || '';
    return !!formErrors.table_name || 
           (trimmedName && isDuplicateTableName(trimmedName, dialogState.table?.id));
  }, [formData.table_name, formErrors.table_name, isDuplicateTableName, dialogState.table]);

  // ============================================
  // SESSION MANAGEMENT - Auto-refresh token
  // ============================================

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();

      // If less than 2 minutes until expiry, refresh automatically
      if (timeLeft > 0 && timeLeft < 120000) {
        console.log('[LocationTables] Auto-refreshing token...');
        await refreshAuthToken();
      }
    };

    // Check every minute
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, [refreshAuthToken]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // ============================================
  // RENDER
  // ============================================

  if (isLoading || isRefreshing) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Preloader text={`Loading ${locationName} Restaurant Tables...`} size={70} fullScreen={true} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <SidebarComponent activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />
            
            <div className="epos-content">
              {/* Hero Card with Location Info */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 'bold' }}>
                    {locationConfig.icon} {locationName} Tables
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                    Overview
                    </div>
                  </div>
                </div>
                <button 
                  className="new-order-btn" 
                  onClick={() => handleOpenDialog('create')}
                  style={{ backgroundColor: locationColor }}
                >
                  <AddIcon style={{ marginRight: '8px' }} />
                  Add Table
                </button>
              </div>

              {/* Stats Cards - Memoized */}
              <StatsCard stats={stats} locationColor={locationColor} />

              {/* Loading Indicator */}
              {loadingTables && (
                <div style={{ width: '100%', marginBottom: '16px' }}>
                  <LinearProgress color="primary" />
                </div>
              )}

              {/* Table Cards Grid */}
              {filteredTables.length === 0 && !loadingTables ? (
                <div className="bottom-grid">
                  <div className="panel" style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <TableIcon style={{ fontSize: 60, color: '#94a3b8', marginBottom: '16px' }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No {locationName} Location Tables Found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      There are currently no tables for the {locationName} location. Start by adding your first table.
                    </Typography>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bottom-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                    {filteredTables
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((table) => (
                        <TableCard
                          key={table.id}
                          table={table}
                          onView={() => handleOpenDialog('view', table)}
                          onEdit={() => handleOpenDialog('edit', table)}
                          onDelete={handleOpenDeleteDialog}
                          getStatusChip={getStatusChip}
                        />
                      ))}
                  </div>
                  
                  {/* Pagination */}
                  {filteredTables.length > rowsPerPage && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                      <TablePagination
                        component="div"
                        count={filteredTables.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* Create/Edit/View Dialog - Optimized */}
      <Dialog
        open={dialogState.open}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {dialogState.mode === 'create' && <AddIcon color="primary" />}
              {dialogState.mode === 'edit' && <EditIcon color="info" />}
              {dialogState.mode === 'view' && <ViewIcon color="primary" />}
              <Typography variant="h6" fontWeight="bold">
                {dialogState.mode === 'create' && `Create New Table - ${locationName} Location`}
                {dialogState.mode === 'edit' && `Edit Table - ${locationName} Location`}
                {dialogState.mode === 'view' && `Table Details - ${locationName} Location`}
              </Typography>
            </div>
          </DialogTitle>
          
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Table Name"
                  value={formData.table_name}
                  onChange={(e) => handleFormChange('table_name', e.target.value)}
                  error={hasTableNameError()}
                  helperText={getTableNameHelperText()}
                  disabled={dialogState.mode === 'view'}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TableRestaurantIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                  autoFocus={dialogState.mode !== 'view'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={`${locationName} (ID: ${locationIdValue})`}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.status} disabled={dialogState.mode === 'view'} required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                    label="Status"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {STATUS_CONFIG[opt.value]?.label || opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.status && (
                    <FormHelperText>{formErrors.status}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => handleFormChange('capacity', parseInt(e.target.value) || '')}
                  error={!!formErrors.capacity}
                  helperText={formErrors.capacity}
                  disabled={dialogState.mode === 'view'}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <GroupIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    inputProps: {
                      min: 1,
                      max: 20
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  disabled={dialogState.mode === 'view'}
                  multiline
                  rows={3}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignItems: 'flex-start', pt: 1 }}>
                        <DescriptionIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Created Date"
                  type="date"
                  value={formData.created_date}
                  onChange={(e) => handleFormChange('created_date', e.target.value)}
                  error={!!formErrors.created_date}
                  helperText={formErrors.created_date}
                  disabled={dialogState.mode === 'view'}
                  required
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} color="inherit">
              {dialogState.mode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {dialogState.mode !== 'view' && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={dialogState.mode === 'create' ? <AddIcon /> : <EditIcon />}
                disabled={submitting || hasTableNameError()}
              >
                {submitting ? <CircularProgress size={24} /> : (dialogState.mode === 'create' ? 'Create' : 'Update')}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog - Optimized */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6" fontWeight="bold">
            Delete Table - {locationName} Location
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete table <strong>"{deleteDialog.table?.table_name}"</strong> from the {locationName} location?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This action cannot be undone. All associated data will be permanently removed.
          </Typography>
          {deleteDialog.table?.status?.toUpperCase() === TABLE_STATUS.OCCUPIED && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This table is currently occupied. Please clear the table before deleting.
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteTable(deleteDialog.table?.id)}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deleteDialog.deleting || deleteDialog.table?.status?.toUpperCase() === TABLE_STATUS.OCCUPIED}
          >
            {deleteDialog.deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add table"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: { xs: 'flex', sm: 'none' },
          zIndex: 1000,
          backgroundColor: locationColor,
          '&:hover': {
            backgroundColor: locationColor,
            opacity: 0.8
          }
        }}
        onClick={() => handleOpenDialog('create')}
      >
        <AddIcon />
      </Fab>
    </Suspense>
  );
};

export default LocationTables;